import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { ModeratorCopy } from "./moderator-copy";

interface RejectTestDialogProps {
  open: boolean;
  score: string;
  busy: boolean;
  copy: ModeratorCopy;
  cancelLabel: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * A test under the pass mark is rejected with a reason: the server refuses a
 * rejection without one, and the candidate reads it.
 */
export function RejectTestDialog({ open, score, busy, copy, cancelLabel, onCancel, onConfirm }: RejectTestDialogProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const fieldId = useId();

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);

  const missing = reason.trim().length === 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onCancel()}>
      <DialogContent className="max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (!missing) onConfirm(reason.trim());
          }}
        >
          <DialogHeader>
            <DialogTitle>{copy.rejectReasonTitle}</DialogTitle>
            <DialogDescription>{copy.rejectReasonDescription(score)}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <label htmlFor={fieldId} className="sr-only">{copy.rejectReasonTitle}</label>
            <Textarea
              id={fieldId}
              autoFocus
              value={reason}
              maxLength={500}
              placeholder={copy.rejectReasonPlaceholder}
              aria-invalid={touched && missing}
              onChange={(event) => setReason(event.target.value)}
            />
            {touched && missing ? <p className="text-xs text-danger">{copy.rejectReasonRequired}</p> : null}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
            <Button type="submit" variant="destructive" size="sm" disabled={busy}>{copy.rejectReasonConfirm}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
